import { css } from '@emotion/react';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonComponent, IconButtonComponent,
} from '~/_shared/baseComponents/buttons';
import { GoogleMapComponent } from '~/_shared/components/google-map/google-map.component';
import type { GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import type { Theme } from '~/_shared/themes/theme.model';
import type { ChartItem } from '~/charts/chartItem/chartItem.types';
import { ChartsGalleryComponent } from '~/charts/chartsGallery.component';
import {
  TableComponent, TableDataCellComponent, TableHeaderCellComponent, TableHeaderComponent, TableRowComponent,
} from '~/map/mapMigration/table';
import { exportPdfDisable } from '~/store/mapSettings/toolsState/exportPdf/exportPdfToolState.actionCreators';

type MapPdfSummaryProps = Readonly<{
  className?: string;
}>;

const columnWidths = ['auto', 'auto', 'auto', 'auto'];

const tableStyle = css({
  width: 900,
});

const rowStyle = (theme: Theme) => css({
  background: theme.modalColors.contentBackground,
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  breakInside: 'avoid',
});

const printHiddenStyle = css`
  @media print {
    display: none;
  }
`;

const generateData = function (amount: number) {
  const result = [];

  for (let i = 0; i < amount; i += 1) {
    result.push({
      id: i,
      name: 'Population',
      value1: 20485861 + i,
      value2: 755861 + 1,
      value3: 8541 + 1,
    });
  }
  return result;
};

const citymap = [
  {
    center: { lat: 41.878, lng: -87.629 },
    population: 2714856,
  },
  {
    center: { lat: 40.714, lng: -74.005 },
    population: 8405837,
  },
  {
    center: { lat: 34.052, lng: -118.243 },
    population: 3857799,
  },
  {
    center: { lat: 49.25, lng: -123.1 },
    population: 603502,
  },
];

export const MapPdfSummaryComponent = (_props: MapPdfSummaryProps) => {
  const [map, setMap] = useState<GoogleMapInstance>();
  const dispatch = useDispatch();

  const closePdfSummary = () => {
    dispatch(exportPdfDisable());
  };

  const populationData = generateData(100);

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMap(map);

    setTimeout(() => {
      (window as any).isReportFullyLoaded = true;
    }, 1000);
  }, []);

  // ADD CIRCLES TO THE MAP
  useEffect(() => {
    if (!map) {
      return;
    }

    map.instance.setCenter({
      lat: 39.828127,
      lng: -98.579404,
    });
    map.instance.setZoom(4);

    for (const city of citymap) {
      // Add the circle for this city to the map.
      new google.maps.Circle({
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        map: map.instance,
        center: city.center,
        radius: Math.sqrt(city.population) * 100,
      });
    }
  }, [map]);

  const chartItems: ChartItem[] = [{
    id: 'chart-1',
    isLoading: false,
    isOutdated: false,
    title: 'Chart of the year',
    metrics: {
      averages: [{
        name: 'Avg',
        value: 20,
      }],
      barValues: [{
        name: 'val 1',
        values: [40, 78],
      }],
      labels: ['label 1', 'label 2'],
      medians: [{
        name: 'median 1',
        value: 50,
      }],
    },
  }];

  return (
    <div
      css={css({ padding: 10, position: 'relative' })}
    >
      <div css={printHiddenStyle}>
        <ButtonComponent text="Download PDF summary" />
      </div>

      <div>
        <IconButtonComponent icon={faClose} onClick={closePdfSummary} />

        <div css={css({ width: 800, height: 400, breakInside: 'avoid' })}>
          <GoogleMapComponent onMapInitialized={handleMapInit} />
        </div>

        <div css={{ margin: '20px 0', breakInside: 'avoid' }}>
          <ChartsGalleryComponent
            chartItems={chartItems}
          />
        </div>

        <div>
          <TableComponent columnWidths={columnWidths} css={tableStyle}>
            <TableHeaderComponent>
              <TableHeaderCellComponent>
                Demographic
              </TableHeaderCellComponent>
              <TableHeaderCellComponent>
                Radius 5km
              </TableHeaderCellComponent>
              <TableHeaderCellComponent>
                Radius 10km
              </TableHeaderCellComponent>
              <TableHeaderCellComponent>
                Radius 15km
              </TableHeaderCellComponent>
            </TableHeaderComponent>

            {populationData.map(data => (
              <TableRowComponent css={rowStyle} key={data.id}>
                <TableDataCellComponent>
                  {data.name}
                </TableDataCellComponent>
                <TableDataCellComponent>
                  {data.value1}
                </TableDataCellComponent>
                <TableDataCellComponent>
                  {data.value2}
                </TableDataCellComponent>
                <TableDataCellComponent>
                  {data.value3}
                </TableDataCellComponent>
              </TableRowComponent>
            ))}
          </TableComponent>
        </div>
      </div>
    </div>
  );
};
